/**
 * Deal Service
 * Fetches deal/opportunity data from Snowflake Data Lake via API
 */

import { api } from './api';
import { Deal } from '../types';

/**
 * Snowflake Deal from API
 * Extended Deal interface with additional Snowflake fields
 */
export interface SnowflakeDeal extends Deal {
  dealStatus: string;
  dealCreatedBy: string;
  status?: string;
  endUserContactName?: string;
  endUserEmail?: string;
  sponsorValid?: boolean;
}

/**
 * Response for deals query
 */
export interface DealsResponse {
  deals: SnowflakeDeal[];
  total: number;
}

/**
 * Fetch deals from Snowflake Data Lake for the authenticated user
 * Returns deals created in the last 12 months
 *
 * The email is extracted from the JWT token on the server side,
 * so no email parameter is needed from the client.
 *
 * @param requestId Optional request ID - if provided, verifies user owns the request
 */
export async function fetchDeals(requestId?: string): Promise<DealsResponse> {
  const queryParams = new URLSearchParams();

  // Only add requestId if provided (allows ownership verification for existing requests)
  if (requestId) {
    queryParams.append('requestId', requestId);
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/deals?${queryString}` : '/deals';

  return api.get<DealsResponse>(url);
}

/**
 * Convert SnowflakeDeal to Deal interface for backward compatibility
 * This maps the Snowflake-specific fields to the existing Deal interface
 * NOTE: Address data is not used - site address is entered separately by the user
 */
export function toBasicDeal(snowflakeDeal: SnowflakeDeal): Deal {
  return {
    opportunityId: snowflakeDeal.opportunityId,
    dealRegistrationNumber: snowflakeDeal.dealRegistrationNumber,
    opportunityName: snowflakeDeal.opportunityName,
    opportunityNumber: snowflakeDeal.opportunityNumber,
    endUserCompanyName: snowflakeDeal.endUserCompanyName,
    opportunityCreatedDate: snowflakeDeal.opportunityCreatedDate,
    vertivRepresentativeName: snowflakeDeal.vertivRepresentativeName,
    vertivRepresentativeEmail: snowflakeDeal.vertivRepresentativeEmail,
    sponsorValid: snowflakeDeal.sponsorValid,
  };
}

/**
 * Deal service object for convenient imports
 */
export const dealService = {
  fetchDeals,
  toBasicDeal,
};

export default dealService;
